import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Info } from "lucide-react";
import { api } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolves relative /api/uploads/* URLs to absolute for preview, leaves http(s) untouched
export const resolveImg = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
    return `${BACKEND_URL}${url}`;
};

export const ImageUploader = ({ label, value, onChange, recommended, testId, accept = "image/*", aspect = "aspect-[4/5]" }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [showTip, setShowTip] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image is over 8MB. Please choose a smaller file.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
            toast.success("Image uploaded.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Upload failed.";
            toast.error(typeof detail === "string" ? detail : "Upload failed.");
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <div className="mb-5" data-testid={`uploader-${testId}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body">{label}</div>
                {recommended && (
                    <div
                        className="relative"
                        onMouseEnter={() => setShowTip(true)}
                        onMouseLeave={() => setShowTip(false)}
                    >
                        <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-[11px] text-oth-ink3 hover:text-oth-blue transition-colors font-body"
                            data-testid={`uploader-tip-${testId}`}
                        >
                            <Info size={12} /> Recommended size
                        </button>
                        {showTip && (
                            <div className="absolute right-0 top-6 z-30 w-[260px] rounded-2xl bg-[#0A0A0A] text-white p-3.5 text-[11px] font-body leading-relaxed shadow-xl">
                                {recommended}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className={`relative rounded-2xl overflow-hidden border border-oth-line/70 bg-white ${aspect} max-w-[260px]`}>
                {value ? (
                    <>
                        <img src={resolveImg(value)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black"
                            aria-label="Remove image"
                            data-testid={`uploader-clear-${testId}`}
                        >
                            <X size={14} />
                        </button>
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-oth-ink3">
                        <Upload size={18} />
                        <span className="mt-2 text-xs font-body">No image</span>
                    </div>
                )}
            </div>

            <div className="mt-3 flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A0A0A] text-white text-xs font-body hover:bg-black/85 disabled:opacity-60 transition-colors"
                    data-testid={`uploader-upload-${testId}`}
                >
                    <Upload size={12} /> {uploading ? "Uploading..." : value ? "Replace" : "Upload image"}
                </button>
                {value && (
                    <span className="text-[11px] text-oth-ink3 font-body truncate max-w-[200px]" title={value}>
                        {value.startsWith("/api/uploads/") ? "Uploaded · " + value.slice(13, 21) + "…" : value}
                    </span>
                )}
            </div>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid={`uploader-input-${testId}`}
            />
        </div>
    );
};
